/* eslint-disable react-refresh/only-export-components */
import { Column, Table } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/custom/button";
import { useTooltipProps } from "@/components/custom/tooltip";
import { cn } from "@/lib/utils";

/** What the panel calls a column: its header when that is text, else `meta.label`, else the id. */
export const columnLabel = (column: Column<any, any>): string => {
  const header = column.columnDef.header;
  if (typeof header === "string" && header.trim()) {
    return header;
  }
  return column.columnDef.meta?.label ?? column.id;
};

export type ColumnManagerPanelProps<TData> = {
  table: Table<TData>;
  onReset: () => void;
  isResetting?: boolean;
};

/** The list of columns with a visibility checkbox each, plus "Reset to default". Container-agnostic. */
export function ColumnManagerPanel<TData>({
  table,
  onReset,
  isResetting,
}: ColumnManagerPanelProps<TData>) {
  const { t } = useTranslation();
  const tooltipProps = useTooltipProps();
  const columns = table
    .getAllLeafColumns()
    .filter((column) => column.getCanHide());
  const visibleCount = columns.filter((column) => column.getIsVisible()).length;

  return (
    <div
      data-component="data-table-column-manager-panel"
      className="flex flex-col gap-3"
    >
      <ul className="flex max-h-[60vh] flex-col gap-0.5 overflow-y-auto pr-1">
        {columns.map((column) => {
          const checked = column.getIsVisible();
          const isLastVisible = checked && visibleCount === 1;
          const inputId = `column-manager-${column.id}`;
          return (
            <li
              key={column.id}
              className={cn(
                "flex items-center gap-3 rounded-md px-2 py-1.5",
                !isLastVisible && "hover:bg-accent",
              )}
              {...(isLastVisible
                ? {
                    ...tooltipProps,
                    "data-tooltip-content": t("lastVisibleColumn"),
                  }
                : {})}
            >
              <Checkbox
                id={inputId}
                checked={checked}
                disabled={isLastVisible}
                onCheckedChange={(value) =>
                  column.toggleVisibility(value === true)
                }
              />
              <label
                htmlFor={inputId}
                className={cn(
                  "flex-1 select-none text-sm",
                  isLastVisible
                    ? "cursor-not-allowed text-muted-foreground"
                    : "cursor-pointer",
                )}
              >
                {columnLabel(column)}
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="link"
          size="sm"
          loading={isResetting}
          onClick={onReset}
        >
          {t("resetToDefault")}
        </Button>
      </div>
    </div>
  );
}
